import { useDispatch, useSelector, useStore } from 'react-redux'
import { useCoreContext } from 'lib/core/hooks'
import { addConfigurableProduct, addSimpleProduct, addGiftCardProduct } from 'lib/core/data/quote/addProduct'
import { applyCoupon } from 'lib/core/data/quote/couponApply'
import { removeCoupon } from 'lib/core/data/quote/couponRemove'
import { getCartId } from 'lib/core/data/quote/cartId/selectors'
import { getCart } from 'lib/core/data/quote/cart/selectors'
import { SimpleProductItem, ConfigurableProductItem } from 'lib/core/data/quote/addProduct/types'
import { CartProcessors } from 'lib/core/data/quote/cart/types'
import { PRODUCT_TYPENAME } from 'lib/core/resources/constants'
import { RootState } from 'lib/core/data/reducer'
import { Callback } from 'lib/types'

interface CartHookOptions {
  callback?: Callback
}

export const useCartId = () => useSelector(getCartId)

export const useCart = () => {
  const { data } = useCoreContext()
  const processors: CartProcessors = {
    cartProcessor: data?.cart?.processor,
    cartItemProcessor: data?.cartItems?.processor,
    shippingAddressProcessor: data?.shippingAddress?.processor,
    availableShippingMethodProcessor: data?.availableShippingMethod?.processor,
    selectedShippingMethodProcessor: data?.selectedShippingMethod?.processor
  }

  const dispatch = useDispatch()
  const state: RootState = useStore().getState()
  const cartId = useSelector(getCartId)
  const cart = useSelector(getCart(cartId, processors))

  const cartItemIsUpdating = (): boolean => {
    return Object.keys(state.quote.cartItemUpdate).reduce((value: boolean, key) => {
      return value || state.quote.cartItemUpdate[key]?.isFetching
    }, false)
  }

  const cartItemIsRemoving = (): boolean => {
    return Object.keys(state.quote.cartItemRemove).reduce((value: boolean, key) => {
      return value || state.quote.cartItemRemove[key]?.isFetching
    }, false)
  }

  const cartItemIsAdding = (): boolean => {
    return Object.keys(state.quote.addProduct).reduce((value: boolean, key) => {
      return value || state.quote.addProduct[key]?.isFetching
    }, false)
  }

  const getCartIsLoading = (): boolean => {
    return Boolean(!cart || cart.isFetching || cartItemIsUpdating() || cartItemIsRemoving() || cartItemIsAdding())
  }

  const isCartBought = cart?.data?.cartBought
  const isFailure = cart?.isFailure
  const isSuccess = cart?.isSuccess
  const isCartException = cart?.isCartException
  const isNotLoaded = !cart || !cart.data
  const isFirstLoad = cart?.isFirstLoad
  const count = cart?.count ?? 0

  const hasPhysicalProducts = !!cart?.data?.items.some(item => item.product.typeName !== PRODUCT_TYPENAME.GIFT_CARD && item.product.typeName !== PRODUCT_TYPENAME.VIRTUAL)

  return {
    cartId,
    cart, // TODO: cart should be cart.data
    _count: count,
    count: isCartBought ? 0 : count,
    isCartException,
    isSuccess,
    isFailure,
    isFirstLoad,
    isCartBought,
    isNotLoaded,
    hasPhysicalProducts,

    getCartIsLoading,

    applyCoupon: (params: {
      code: string
      options?: CartHookOptions
    }) => {
      if (!cartId) {
        return
      }

      dispatch(applyCoupon(cartId, params.code, params.options?.callback)) // TODO: named params.
    },

    removeCoupon: (params?: {
      options?: CartHookOptions
    }) => {
      if (!cartId) {
        return
      }

      dispatch(removeCoupon(cartId, params?.options?.callback)) // TODO: named params.
    },

    addSimpleProduct: (params: {
      sku: string
      cartItems: SimpleProductItem[]
      options?: CartHookOptions
    }) => {
      dispatch(addSimpleProduct( // TODO: named params.
        cartId,
        params.sku,
        params.cartItems,
        params.options?.callback
      ))
    },

    addConfigurableProduct: (params: {
      configurableSku: string
      cartItems: ConfigurableProductItem[]
      simpleProductsItems?: SimpleProductItem[]
      options?: CartHookOptions
    }) => {
      dispatch(addConfigurableProduct( // TODO: named params.
        cartId,
        params.configurableSku,
        params.cartItems,
        params.simpleProductsItems,
        params.options?.callback
      ))
    },

    addGiftCardProduct: (params: {
      cartId: string
      sku: string
      cartItems: SimpleProductItem[]
      message: string
      senderName: string
      senderEmail: string
      recipientName: string
      recipientEmail: string
      giftCardAmount: string
      productId: string
      options?: CartHookOptions
    }) => {
      return dispatch(addGiftCardProduct( // TODO: named params.
        cartId,
        params.sku,
        params.cartItems,
        params.message,
        params.senderName,
        params.senderEmail,
        params.recipientName,
        params.recipientEmail,
        params.giftCardAmount,
        params.productId,
        params.options?.callback
      ))
    }
  }
}
